import {
  Box,
  Stack,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";

interface DayOfWeekTimesEditorProps {
  value: Record<string, { startTime: string; endTime: string }>;
  onChange: (v: Record<string, { startTime: string; endTime: string }>) => void;
}

export const DayOfWeekTimesEditor = ({
  value,
  onChange,
}: DayOfWeekTimesEditorProps) => {
  const days = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  const setTime = (day: string, key: "startTime" | "endTime", t: string) =>
    onChange({
      ...(value || {}),
      [day]: { ...(value?.[day] || { startTime: "", endTime: "" }), [key]: t },
    });

  // Generate only 30-minute stepped times as strings, e.g. ["00:00","00:30",...,"23:30"]
  const timeOptions: string[] = [];
  for (let h = 0; h < 24; ++h) {
    for (let m = 0; m < 60; m += 30) {
      timeOptions.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }

  return (
    <Box sx={{ border: 1, borderColor: "divider", p: 2, borderRadius: 2 }}>
      <Stack spacing={1}>
        {days.map((d) => {
          const selectedStart = value?.[d]?.startTime || "";
          // End options: only times >= start+30 min
          let endOptions = timeOptions;
          if (selectedStart) {
            const [h, m] = selectedStart.split(":").map(Number);
            const minMins = h * 60 + m + 30;
            endOptions = timeOptions.filter(str => {
              const [eh, em] = str.split(":").map(Number);
              return (eh * 60 + em) >= minMins;
            });
          }
          return (
            <Stack key={d} direction="row" spacing={1} alignItems="center">
              <Typography
                variant="body2"
                sx={{ minWidth: 100, textTransform: "capitalize" }}
              >
                {d}
              </Typography>
              <FormControl sx={{ minWidth: 120 }} size="small">
                <InputLabel>Start Time</InputLabel>
                <Select
                  value={selectedStart}
                  label="Start Time"
                  onChange={e => setTime(d, "startTime", e.target.value)}
                >
                  <MenuItem value="">--:--</MenuItem>
                  {timeOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 120 }} size="small">
                <InputLabel>End Time</InputLabel>
                <Select
                  value={value?.[d]?.endTime || ""}
                  label="End Time"
                  onChange={e => setTime(d, "endTime", e.target.value)}
                  disabled={!selectedStart}
                >
                  <MenuItem value="">--:--</MenuItem>
                  {endOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
};
